// ArChat Auto-Normalized File
// Created: 12-03-2026 22:38:36
// Author: SIVOL® - Aldo Frias - Argentina

/**
 * ErrorBoundary - Captura errores de React para observabilidad
 * Muestra stacktrace, componentStack, props y request_id. Envía a POST /api/logs/frontend-error.php
 */

import React from 'react';
import { API_ENDPOINTS, joinApiOrigin } from '../utils/constants';
import { request as apiClientRequest } from '../services/apiClient';
import { errorBus } from '../core/errors/errorBus';
import { captureRenderCrash } from '../utils/runtimeMonitor';

function tr(key, fallback) {
  try {
    const translator = window?.__ARCHAT_I18N__?.t;
    if (typeof translator === 'function') {
      const value = translator(key);
      if (value && value !== key) return value;
    }
  } catch {
    // noop
  }
  return fallback;
}

function getFrontendErrorUrl() {
  return joinApiOrigin(API_ENDPOINTS.FRONTEND_ERROR_LOG || '/api/logs/frontend-error.php');
}

const DEV_APP_ENVS = new Set(['development', 'dev', 'local']);
const appEnv = String(import.meta.env?.VITE_APP_ENV || '').toLowerCase();
const mode = String(import.meta.env?.MODE || '').toLowerCase();
const SHOW_TECHNICAL_CRASH_UI = import.meta.env?.DEV === true || DEV_APP_ENVS.has(appEnv) || DEV_APP_ENVS.has(mode);

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null, requestId: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    const requestId = typeof window !== 'undefined' ? window.__archatLastRequestId ?? null : null;

    this.setState({
      error,
      errorInfo,
      requestId
    });

    if (error) {
      console.error('[FULL_ERROR]', error);
      console.error('[STACK]', error?.stack);
      if (error?.stack) {
        console.error('[ERROR BOUNDARY]', error.message, error.stack);
      }
      captureRenderCrash(error, errorInfo);
    }
    try {
      if (typeof window !== 'undefined' && window.__SENTRY__ && typeof window.__SENTRY__.captureException === 'function') {
        window.__SENTRY__.captureException(error);
      }
    } catch (_) {}

    // Enviar a backend (structured log)
    const payload = {
      level: 'error',
      message: error ? `React Error Boundary: ${error.message}` : 'React Error Boundary',
      request_id: requestId,
      stack: error?.stack ?? null,
      componentStack: errorInfo?.componentStack ?? null,
      props: tryStringifyProps(this.props),
      url: typeof window !== 'undefined' ? window.location.href : null,
      user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null
    };

    try {
      errorBus.emit({
        type: 'system',
        code: 'E-UI-BOUNDARY',
        message: error?.message || 'Error inesperado de interfaz',
        requestId,
        path: typeof window !== 'undefined' ? window.location.pathname : '',
        timestamp: new Date().toISOString(),
      });
    } catch (_) {}

    try {
      apiClientRequest(getFrontendErrorUrl(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      }).catch((e) => {
        console.error('🔥 [ERROR BOUNDARY] Fallo al enviar log al backend:', e);
      });
    } catch (e) {
      console.error('🔥 [ERROR BOUNDARY] Error enviando log:', e);
    }
  }

  render() {
    if (this.state.hasError) {
      const { error, errorInfo, requestId } = this.state;
      const stack = error?.stack ?? '';
      const componentStack = errorInfo?.componentStack ?? '';

      if (!SHOW_TECHNICAL_CRASH_UI) {
        const copyText = [
          `Codigo: E-UI-BOUNDARY`,
          requestId ? `X-Request-ID: ${requestId}` : '',
        ].filter(Boolean).join('\n');
        return (
          <div style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.65)',
            zIndex: 999999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px'
          }}>
            <div style={{
              width: 'min(560px, 100%)',
              background: '#111827',
              color: '#f9fafb',
              borderRadius: '12px',
              border: '1px solid #374151',
              padding: '20px',
              fontFamily: 'system-ui, -apple-system, Segoe UI, Roboto, sans-serif'
            }}>
              <h2 style={{ marginTop: 0, marginBottom: '10px', fontSize: '20px' }}>
                {tr('errors.modal.title', 'Error')}
              </h2>
              <p style={{ marginTop: 0, marginBottom: '10px' }}>
                {tr('errors.unexpected', 'Ocurrió un error inesperado. Intentá nuevamente.')}
              </p>
              {requestId && (
                <p style={{ marginTop: 0, marginBottom: '16px', opacity: 0.9 }}>
                  <strong>{tr('errors.boundary.request_id', 'X-Request-ID:')}</strong> {requestId}
                </p>
              )}
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                <button
                  type="button"
                  onClick={() => window.location.reload()}
                  style={{ padding: '10px 14px', borderRadius: '8px', border: 'none', cursor: 'pointer' }}
                >
                  {tr('errors.boundary.reload_page', 'Recargar página')}
                </button>
                <button
                  type="button"
                  onClick={() => navigator.clipboard?.writeText(copyText)}
                  style={{ padding: '10px 14px', borderRadius: '8px', border: '1px solid #4b5563', background: 'transparent', color: '#f9fafb', cursor: 'pointer' }}
                >
                  {tr('errors.modal.copyDetails', 'Copiar detalle')}
                </button>
              </div>
            </div>
          </div>
        );
      }

      return (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: '#1a1a1a',
          color: 'var(--color-text-primary)',
          padding: '24px',
          zIndex: 999999,
          overflow: 'auto',
          fontFamily: 'monospace',
          fontSize: '14px'
        }}>
          <div style={{ marginBottom: '16px', fontWeight: 'bold', fontSize: '18px' }}>
            {tr('errors.boundary.title', 'ERROR DE REACT CAPTURADO')}
          </div>
          {requestId && (
            <div style={{ marginBottom: '8px' }}>
              <strong>{tr('errors.boundary.request_id', 'X-Request-ID:')}</strong> <code>{requestId}</code>
              <div style={{ fontSize: '12px', opacity: 0.8 }}>{tr('errors.boundary.correlate', 'Correlacionar con backend/logs/structured.log')}</div>
            </div>
          )}
          <div style={{ marginBottom: '12px' }}>
            <strong>{tr('errors.boundary.message_label', 'Mensaje:')}</strong> {error?.toString()}
          </div>
          <details style={{ marginBottom: '12px' }}>
            <summary>{tr('errors.boundary.stack_trace', 'Stack trace')}</summary>
            <pre style={{ background: '#000', padding: '10px', overflow: 'auto', maxHeight: '200px', whiteSpace: 'pre-wrap' }}>
              {stack || tr('errors.boundary.empty', '(vacío)')}
            </pre>
          </details>
          <details style={{ marginBottom: '12px' }}>
            <summary>{tr('errors.boundary.component_stack', 'Component stack')}</summary>
            <pre style={{ background: '#000', padding: '10px', overflow: 'auto', maxHeight: '200px', whiteSpace: 'pre-wrap' }}>
              {componentStack || tr('errors.boundary.empty', '(vacío)')}
            </pre>
          </details>
          <button
            onClick={() => window.location.reload()}
            style={{
              marginTop: '12px',
              padding: '10px 20px',
              fontSize: '16px',
              cursor: 'pointer',
              backgroundColor: '#fff',
              color: '#000',
              border: 'none',
              borderRadius: '4px'
            }}
          >
            {tr('errors.boundary.reload_page', 'Recargar página')}
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

function tryStringifyProps(props) {
  if (!props || typeof props !== 'object') return null;
  try {
    const safe = {};
    for (const [k, v] of Object.entries(props)) {
      if (k === 'children' && typeof v === 'object') {
        safe[k] = '[ReactNode]';
      } else {
        safe[k] = typeof v === 'function' ? '[Function]' : v;
      }
    }
    return safe;
  } catch {
    return null;
  }
}

export default ErrorBoundary;
